import { useCallback } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Button, Card, Header, Icon, Message } from "semantic-ui-react"

import { UserChip } from "components/lib/Chips"
import { useAppSelector } from "store"
import util from "utils/utils"

import InviteeChip from "./InviteeChip"
import type { InviteCardProps } from "./types"

const IdeaInviteCard = ({ invitation, acceptInvite, rejectInvite, removeInvite, cardView = true }: InviteCardProps) => {
  const { inviterUser, inviteeUser, invitee, idea, createdByCurrentUser } = invitation
  const user = useAppSelector((state) => state.user)
  const { t } = useTranslation()

  const accept = useCallback(() => {
    util
      .confirm(
        `Join ${t("generic.idea")} team`,
        `Are you sure you want to collaborate with ${inviterUser?.profile?.fullName} on the ${t("generic.idea")} "${idea.name}"?`,
      )
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            toast.success(`Joined ${t("generic.idea")} team`)
          },
          () => toast.error(`Error joining ${t("generic.idea")} team`),
        )
      })
      .catch(() => {})
  }, [acceptInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t])

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject join",
        `Are you sure you want to reject ${inviterUser?.profile?.fullName} invitation to join the ${t("generic.idea")} "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected")
          },
          () => toast.error("Error rejecting invite"),
        )
      })
      .catch(() => {})
  }, [rejectInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t])

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove invitation",
        `Are you sure you want to remove your invitation to let ${inviteeUser?.profile?.fullName || invitee} join your ${t("generic.idea")} "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed")
          },
          () => toast.error("Error removing invite"),
        )
      })
      .catch(() => {})
  }, [removeInvite, invitation, idea?.name, inviteeUser?.profile?.fullName, invitee, t])

  if (!idea) {
    invitation.canAccept = false
  }

  return (
    <div style={cardView ? { display: "flex", justifyContent: "space-between", alignItems: "center" } : {}}>
      <div>
        {cardView ? (
          <Header as={Link} disabled={!idea} to={idea ? `/ideas/${idea._id}` : null}>
            {idea?.name || `Deleted ${t("generic.idea")}`}
          </Header>
        ) : (
          <Header as="h2" icon textAlign="center">
            <Icon name="users" circular />
            <Header.Content>Join {idea?.name || `Deleted ${t("generic.idea")}`}</Header.Content>
          </Header>
        )}
        <Card.Description>
          {createdByCurrentUser ? (
            <InviteeChip invitation={invitation} preText="You invited" postText={`to join this ${t("generic.idea")}`} />
          ) : (
            <UserChip
              compact="very"
              user={inviterUser}
              postText={`has invited you to join this ${t("generic.idea")}`}
            />
          )}
        </Card.Description>
      </div>
      <Card.Content extra>
        <div className="ui" style={cardView ? {} : { marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          {invitation.acceptedAt && idea && (
            <Button basic color="green" as={Link} to={`/ideas/${idea?._id}`}>
              Go to {t("generic.idea")}
            </Button>
          )}

          {!invitation.acceptedAt && (
            <>
              {!idea && (
                <Message
                  style={{ width: "100%" }}
                  error
                >{`This invitation to join is no longer valid because the ${t("generic.idea")} has been deleted.`}</Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  basic
                  color={createdByCurrentUser ? null : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && invitation.canAccept ? (
                <Button basic color="green" onClick={accept}>
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  )
}

export default IdeaInviteCard
